import React from 'react';
import { COPY } from '../copy';
import { bandFor, ratioPct } from '../lib/sufficiency';
import type { SufficiencySummary } from '../types';
import { BandLegend, BandPill, InfoTip, Meter } from './parts';

const RING = 64;
const STROKE = 5;

function ScoreRing({ score }: { score: number | null }) {
  const r = (RING - STROKE) / 2 - 1;
  const c = 2 * Math.PI * r;
  const pct = score == null ? 0 : Math.max(0, Math.min(100, score)) / 100;
  return (
    <div className="ns-gauge" aria-hidden>
      <svg width={RING} height={RING}>
        <circle className="ns-gauge-track" cx={RING / 2} cy={RING / 2} r={r} fill="none" strokeWidth={STROKE} />
        <circle
          className="ns-gauge-arc"
          cx={RING / 2}
          cy={RING / 2}
          r={r}
          fill="none"
          strokeWidth={STROKE}
          strokeLinecap="round"
          strokeDasharray={`${c * pct} ${c}`}
          transform={`rotate(-90 ${RING / 2} ${RING / 2})`}
        />
      </svg>
      <b>{score ?? '–'}</b>
    </div>
  );
}

export function SufficiencySummaryTiles({ summary, medalScore }: { summary: SufficiencySummary; medalScore: number }) {
  const band = bandFor(summary.average_score);
  return (
    <div className="ns-kpis">
      <div className="ns-kpi ns-kpi-hero">
        <ScoreRing score={summary.average_score} />
        <div>
          <div className="ns-stat-label">
            <span>Average score</span>
            <InfoTip label="the average score" title="Average score" wide>
              <BandLegend intro={COPY.averageHelp} />
            </InfoTip>
          </div>
          <span className="ns-sr">{summary.average_score == null ? 'No score yet' : `${summary.average_score} out of 100`}</span>
          <div className="ns-hero-line">{band ? <BandPill band={band} /> : <span className="ns-muted">No meals logged</span>}</div>
        </div>
      </div>
      <div className="ns-kpi">
        <div className="ns-stat-label">
          <span>Days logged</span>
          <InfoTip label="days logged" title="Days logged">
            {COPY.daysLoggedHelp}
          </InfoTip>
        </div>
        <div className="ns-stat-val">
          {summary.days_logged}
        </div>
        <Meter pct={ratioPct(summary.days_logged, summary.days_planned)} />
      </div>
      <div className="ns-kpi">
        <div className="ns-stat-label">
          <span>Medal days</span>
          <InfoTip label="medal days" title="Medal days">
            {COPY.medalDaysHelp(medalScore)}
          </InfoTip>
        </div>
        <div className="ns-stat-val">
          {summary.medal_days}
          <small>of {summary.days_logged}</small>
        </div>
        <Meter pct={ratioPct(summary.medal_days, summary.days_logged)} />
      </div>
    </div>
  );
}
